const getFieldValue = (row, field) => {
  if (!field) return undefined;
  if (!String(field).includes('.')) return row?.[field];
  const segments = String(field)
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

const toNumber = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLabel = (value, fallback = 'Unknown') => {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
};

const groupRows = (rows, field) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = normalizeLabel(getFieldValue(row, field));
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return map;
};

const buildSeriesFromRows = ({ rows, groupField, seriesField, valueField, limit }) => {
  const groupMap = new Map();
  const seriesKeys = new Set();

  rows.forEach((row) => {
    const groupKey = normalizeLabel(getFieldValue(row, groupField));
    const seriesKey = normalizeLabel(getFieldValue(row, seriesField));
    const value = valueField ? toNumber(getFieldValue(row, valueField)) : 1;
    if (!groupMap.has(groupKey)) groupMap.set(groupKey, new Map());
    const bucket = groupMap.get(groupKey);
    bucket.set(seriesKey, (bucket.get(seriesKey) || 0) + (value ?? 0));
    seriesKeys.add(seriesKey);
  });

  const groups = Array.from(groupMap.keys());
  const sortedGroups = groups.sort((a, b) => {
    const totalA = Array.from(groupMap.get(a).values()).reduce((sum, v) => sum + v, 0);
    const totalB = Array.from(groupMap.get(b).values()).reduce((sum, v) => sum + v, 0);
    return totalB - totalA;
  });

  const limitedGroups = typeof limit === 'number' ? sortedGroups.slice(0, limit) : sortedGroups;
  const series = Array.from(seriesKeys).map((seriesKey) => ({
    label: seriesKey,
    data: limitedGroups.map((group) => groupMap.get(group)?.get(seriesKey) || 0),
  }));

  return { groups: limitedGroups, series };
};

const buildLineSeries = ({ rows, xField, yField, seriesField }) => {
  const seriesMap = new Map();
  rows.forEach((row) => {
    const seriesKey = normalizeLabel(seriesField ? getFieldValue(row, seriesField) : 'Series');
    const xValue = normalizeLabel(getFieldValue(row, xField), '');
    const yValue = toNumber(getFieldValue(row, yField));
    if (!xValue || yValue == null) return;
    if (!seriesMap.has(seriesKey)) seriesMap.set(seriesKey, new Map());
    const bucket = seriesMap.get(seriesKey);
    bucket.set(xValue, (bucket.get(xValue) || 0) + yValue);
  });

  const labels = Array.from(
    new Set(
      Array.from(seriesMap.values()).flatMap((bucket) => Array.from(bucket.keys()))
    )
  ).sort();

  const series = Array.from(seriesMap.entries()).map(([label, bucket]) => ({
    label,
    data: labels.map((key) => bucket.get(key) || 0),
  }));

  return { labels, series };
};

const buildHistogram = ({ rows, valueField, bins = 10, min, max }) => {
  const values = rows
    .map((row) => toNumber(getFieldValue(row, valueField)))
    .filter((value) => value != null);
  if (!values.length) return { labels: [], counts: [] };

  const minValue = min ?? Math.min(...values);
  const maxValue = max ?? Math.max(...values);
  const safeBins = Math.max(1, bins);
  const step = (maxValue - minValue) / safeBins || 1;
  const counts = Array(safeBins).fill(0);

  values.forEach((value) => {
    const index = Math.min(Math.floor((value - minValue) / step), safeBins - 1);
    counts[index] += 1;
  });

  const labels = counts.map((_, index) => {
    const start = minValue + index * step;
    const end = start + step;
    return `${start.toFixed(1)}-${end.toFixed(1)}`;
  });

  return { labels, counts };
};

const buildHeatmap = ({ rows, xField, yField, valueField, maxBuckets = 12 }) => {
  const xSet = new Set();
  const ySet = new Set();
  const cellMap = new Map();

  rows.forEach((row) => {
    const xValue = normalizeLabel(getFieldValue(row, xField));
    const yValue = normalizeLabel(getFieldValue(row, yField));
    const value = valueField ? toNumber(getFieldValue(row, valueField)) : 1;
    if (!xValue || !yValue) return;
    xSet.add(xValue);
    ySet.add(yValue);
    const key = `${xValue}__${yValue}`;
    cellMap.set(key, (cellMap.get(key) || 0) + (value ?? 0));
  });

  const xLabels = Array.from(xSet).slice(0, maxBuckets);
  const yLabels = Array.from(ySet).slice(0, maxBuckets);
  const matrix = yLabels.map((yLabel) =>
    xLabels.map((xLabel) => cellMap.get(`${xLabel}__${yLabel}`) || 0)
  );

  return { xLabels, yLabels, matrix };
};

const buildFunnel = ({ rows, stageField, valueField, stages }) => {
  const bucket = new Map();
  rows.forEach((row) => {
    const stage = normalizeLabel(getFieldValue(row, stageField));
    const value = valueField ? toNumber(getFieldValue(row, valueField)) : 1;
    if (!stage) return;
    bucket.set(stage, (bucket.get(stage) || 0) + (value ?? 0));
  });

  const orderedStages = Array.isArray(stages) && stages.length
    ? stages
    : Array.from(bucket.keys()).sort((a, b) => bucket.get(b) - bucket.get(a));

  return orderedStages.map((stage) => ({ stage, value: bucket.get(stage) || 0 }));
};

const buildBoxStats = ({ rows, valueField, groupField }) => {
  const groupMap = groupField ? groupRows(rows, groupField) : new Map([['All', rows]]);
  const toStats = (values) => {
    const sorted = values.slice().sort((a, b) => a - b);
    const median = (arr) => {
      const mid = Math.floor(arr.length / 2);
      if (arr.length % 2 === 0) return (arr[mid - 1] + arr[mid]) / 2;
      return arr[mid];
    };
    const q1 = median(sorted.slice(0, Math.floor(sorted.length / 2)));
    const q3 = median(sorted.slice(Math.ceil(sorted.length / 2)));
    return {
      min: sorted[0],
      max: sorted[sorted.length - 1],
      q1,
      q3,
      median: median(sorted),
    };
  };

  return Array.from(groupMap.entries()).map(([label, groupRowsValue]) => {
    const values = groupRowsValue
      .map((row) => toNumber(getFieldValue(row, valueField)))
      .filter((value) => value != null);
    if (!values.length) return null;
    return { label, ...toStats(values) };
  }).filter(Boolean);
};

const movingAverage = (values, window) => {
  const safeWindow = Math.max(1, window || 3);
  return values.map((_, index) => {
    const start = Math.max(0, index - safeWindow + 1);
    const slice = values.slice(start, index + 1);
    const sum = slice.reduce((acc, value) => acc + value, 0);
    return sum / slice.length;
  });
};

const parseWindowDays = (value) => {
  if (value == null) return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'last_7_days' || raw === '7d') return 7;
  if (raw === 'last_30_days' || raw === '30d') return 30;
  if (raw === 'last_90_days' || raw === '90d') return 90;
  if (raw === 'last_365_days' || raw === '1y' || raw === '365d') return 365;
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  return null;
};

const withinWindow = (value, windowDays) => {
  if (!windowDays) return true;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  return date.getTime() >= cutoff;
};

const filterRowsByWindow = ({ rows, field, window }) => {
  const windowDays = parseWindowDays(window);
  if (!windowDays || !field) return rows;
  return rows.filter((row) => withinWindow(getFieldValue(row, field), windowDays));
};

const DASHBOARD_CHART_PALETTE = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(201, 203, 207, 0.7)',
];

const getChartColor = (index) => {
  const safeIndex = Math.abs(Number(index) || 0) % DASHBOARD_CHART_PALETTE.length;
  return DASHBOARD_CHART_PALETTE[safeIndex];
};

export {
  getFieldValue,
  toNumber,
  normalizeLabel,
  groupRows,
  buildSeriesFromRows,
  buildLineSeries,
  buildHistogram,
  buildHeatmap,
  buildFunnel,
  buildBoxStats,
  movingAverage,
  parseWindowDays,
  filterRowsByWindow,
  DASHBOARD_CHART_PALETTE,
  getChartColor,
};
